import {createGlobalStyle, css, th} from 'quickstart/system'
import {STORYBOOK} from 'tizra'
import {blockStyles, inlineStyles} from './html'

/*
 * Currently this works by rendering <GlobalStyle /> in ThemeBlock. Therefore it
 * only applies to site render via renderBlocks, and does not apply to the Tizra
 * admin. In Storybook, ThemeBlock is rendered by blockDecorator or
 * componentDecorator.
 *
 * In the future we want to switch to Linaria which generates CSS at build-time,
 * rather than styled-components which generates dynamically at run-time. In
 * preparation for that, we need to protect the Tizra admin from any resets we
 * apply here.
 *
 * PRODUCTION:
 * - site render:
 *   - ThemeBlock renders
 *   - scoped to :where(body#t-my)
 * - admin dialogs:
 *   - ThemeBlock doesn't render
 *   - tizra.css applies globally
 *
 * STORYBOOK:
 * - admin stories:
 *   - ThemeBlock doesn't render
 *   - <AdminStory /> applies raw tizra.css globally
 *   - consider moving to adminDecorator
 *   - additionally protected because none of the selectors match
 * - component stories:
 *   - ThemeBlock renders
 *   - scoped to .component-story-body (and .dialog-backdrop)
 *   - admin dialogs scope tizra.css to .tizra-admin-backdrop
 * - block stories:
 *   - ThemeBlock renders
 *   - scoped to .block-story-body (and .dialog-backdrop)
 *   - admin dialogs scope tizra.css to .tizra-admin-backdrop
 */

const bodySelector = STORYBOOK
  ? 'body:where(#t-my), .block-story-body, .component-story-body'
  : 'body:where(#t-my)'

const bodyOrPortalSelector = STORYBOOK
  ? `${bodySelector}, .dialog-backdrop`
  : bodySelector

const reset = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  :focus {
    outline: none !important; /* firefox needs !important */
  }

  /*********************
   * FONTS
   *********************/

  /* :is() for specificity 0-0-1, since wrapper is :where() 0-0-0 */
  &:is(body, div),
  button,
  input,
  select,
  textarea {
    ${th('fonts.ui')};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${th('fonts.heading')};
  }

  /*********************
   * TYPOGRAPHY
   *********************/

  /* :is() for specificity 0-0-1, since wrapper is :where() 0-0-0 */
  &:is(body, div),
  button,
  input,
  select,
  textarea {
    ${th('ui.html')};
  }

  img {
    margin: 0;
    max-width: 100%;
    overflow: hidden;
  }

  /* https://gist.github.com/unruthless/413930 */
  sub,
  sup {
    font-size: 75%;
    line-height: 0;
    margin: 0;
    position: relative;
    vertical-align: baseline;
  }
  sub {
    bottom: -0.25em;
  }
  sup {
    top: -0.5em;
  }

  hr {
    border: 0;
    border-top: ${th('borders.fine')};
    margin: 0;
  }

  strong,
  b {
    font-weight: ${th('fontWeights.bold')};
  }

  em,
  i {
    font-style: italic;
  }

  /*********************
   * COLORS
   *********************/

  /* :is() for specificity 0-0-1, since wrapper is :where() 0-0-0 */
  &:is(body, div) {
    color: ${th('colors.textColor')};
    --evergreen-color-link: ${th('colors.linkColor')};
  }

  /* Don't set background color on dialog backdrop.
   * This exclusion is only for storybook which has the expanded
   * bodyOrPortalSelector.
   */
  &:is(body) > {
    background-color: ${th('colors.backgroundColor')};
  }
  &:when(.dialog-backdrop) .dialog-dialog {
    background-color: ${th('colors.backgroundColor')};
  }

  ::selection {
    background-color: ${th('colors.selectionBgColor')};
    color: ${th('colors.selectionColor')};
  }

  /*********************
   * FORMS
   *********************/

  /* https://css-tricks.com/almanac/properties/a/appearance/ */
  input,
  input[type='search'] {
    margin: 0;
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
  }
  input::-webkit-search-cancel-button {
    display: none;
  }
`

const stickyFooter = css`
  /* :is() for specificity 0-0-1, since wrapper is :where() 0-0-0 */
  &:is(body, div) {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
`

export const GlobalStyle = createGlobalStyle`
  :where(${bodyOrPortalSelector}) {
    ${reset};
    ${stickyFooter};
    .customer-html {
      ${blockStyles};
    }
    .customer-inline {
      ${inlineStyles};
    }
  }
`
